<template>
    <div class="notes-filters-buttons__navigator">
        <div class="notes-filters-buttons__content-row">
            <!-- Botón navegación anterior -->
            <button
v-if="showNavButtons" type="button" class="notes-filters-buttons__scroll-nav-btn prev" :disabled="isAtStart"
                @click="scrollLeft">
                <i class="fas fa-chevron-left"></i>
            </button>

            <!-- Scroll horizontal de notas -->
            <div ref="notesScroll" class="notes-filters-buttons__notes-scroll">
                <div class="notes-filters-buttons__notes-container">
                    <div
v-for="(note, index) in props.filteredNotes" :key="note.id" class="notes-filters-buttons__note-item"
                        :class="{ 'notes-filters-buttons__note-item--active': note.id === props.currentId }" @click="props.selectNote(note.id)">
                        <div class="notes-filters-buttons__note-number">{{ index + 1 }}</div>
                        <div class="notes-filters-buttons__note-date">{{ props.formatDate(note.created_at) }}</div>
                        <div v-if="note.id === props.currentId" class="notes-filters-buttons__active-badge">
                            <i class="fas fa-check-circle"></i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Botón navegación siguiente -->
            <button
v-if="showNavButtons" type="button" class="notes-filters-buttons__scroll-nav-btn next" :disabled="isAtEnd"
                @click="scrollRight">
                <i class="fas fa-chevron-right"></i>
            </button>

            <!-- Separador visual -->
            <div class="notes-filters-buttons__separator"></div>

            <!-- Contador total -->
            <div class="notes-filters-buttons__total-counter">
                <i :class="getIcon()"></i>
                <span class="notes-filters-buttons__counter-number">{{ totalNotes }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    filteredNotes: {
        type: Array,
        default: () => []
    },
    currentId: {
        type: Number,
        default: null
    },
    selectNote: {
        type: Function,
        default: () => { }
    },
    formatDate: {
        type: Function,
        default: () => { }
    },
    recordType: {
        type: String,
        default: 'nota', // 'nota', 'receta', 'documento'
        validator: (value) => ['nota', 'receta', 'documento'].includes(value)
    }
});

const notesScroll = ref(null);
const isAtStart = ref(true);
const isAtEnd = ref(false);
const showNavButtons = ref(false);

const totalNotes = computed(() => {
    return props.filteredNotes.length;
});

const getIcon = () => {
    const icons = {
        'nota': 'fas fa-file-medical',
        'receta': 'fas fa-prescription-bottle-alt',
        'documento': 'fas fa-file-alt'
    };
    return icons[props.recordType] || icons.nota;
};

const checkScroll = () => {
    if (notesScroll.value) {
        const element = notesScroll.value;
        isAtStart.value = element.scrollLeft === 0;
        isAtEnd.value = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;
        showNavButtons.value = element.scrollWidth > element.clientWidth;
    }
};

const scrollLeft = () => {
    if (notesScroll.value) {
        notesScroll.value.scrollBy({ left: -200, behavior: 'smooth' });
    }
};

const scrollRight = () => {
    if (notesScroll.value) {
        notesScroll.value.scrollBy({ left: 200, behavior: 'smooth' });
    }
};

onMounted(() => {
    if (notesScroll.value) {
        notesScroll.value.addEventListener('scroll', checkScroll);
        // Check inicial
        setTimeout(checkScroll, 100);
    }
});

onUnmounted(() => {
    if (notesScroll.value) {
        notesScroll.value.removeEventListener('scroll', checkScroll);
    }
});
</script>

<style scoped>
/* DISEÑO HORIZONTAL MODERNO Y FLUIDO */
.notes-filters-buttons__navigator {
    background: transparent;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    border: none;
}

.notes-filters-buttons__content-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    height: 48px;
}

/* BOTONES DE NAVEGACIÓN */
.notes-filters-buttons__scroll-nav-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    flex-shrink: 0;
}

.notes-filters-buttons__scroll-nav-btn:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    background: #f8fafc;
    color: #64748b;
}

.notes-filters-buttons__scroll-nav-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    box-shadow: none;
}

/* SCROLL HORIZONTAL OCULTO */
.notes-filters-buttons__notes-scroll {
    flex: 1;
    overflow: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* IE/Edge */
    margin: 0;
    padding: 0;
}

.notes-filters-buttons__notes-scroll::-webkit-scrollbar {
    display: none;
    /* Chrome/Safari */
}

.notes-filters-buttons__notes-container {
    display: flex;
    gap: 8px;
    min-width: min-content;
    height: 36px;
    align-items: center;
}

/* ITEM DE NOTA - DISEÑO COMPACTO Y ELEGANTE */
.notes-filters-buttons__note-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: #ffffff;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid #f1f5f9;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    white-space: nowrap;
    flex-shrink: 0;
    height: 36px;
}

.notes-filters-buttons__note-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    background: #fafbfc;
    border-color: #e2e8f0;
}

.notes-filters-buttons__note-item--active {
    background: #fafbfc;
    border-color: #e2e8f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
}

/* NÚMERO DE NOTA */
.notes-filters-buttons__note-number {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: #f8fafc;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.75rem;
    flex-shrink: 0;
    box-shadow: none;
}

.notes-filters-buttons__note-item--active .notes-filters-buttons__note-number {
    background: #e2e8f0;
    color: #64748b;
    box-shadow: none;
}

/* FECHA DE NOTA */
.notes-filters-buttons__note-date {
    font-weight: 600;
    font-size: 0.8125rem;
    color: #64748b;
    letter-spacing: -0.01em;
}

.notes-filters-buttons__note-item--active .notes-filters-buttons__note-date {
    color: #475569;
}

/* BADGE ACTIVO */
.notes-filters-buttons__active-badge {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    flex-shrink: 0;
    box-shadow: 0 1px 3px rgba(16, 185, 129, 0.2);
    animation: notes-filters-buttons__scaleIn 0.3s ease-out;
}

@keyframes notes-filters-buttons__scaleIn {
    from {
        transform: scale(0);
        opacity: 0;
    }

    to {
        transform: scale(1);
        opacity: 1;
    }
}

/* SEPARADOR */
.notes-filters-buttons__separator {
    width: 1px;
    height: 24px;
    background: #e2e8f0;
    border-radius: 1px;
    flex-shrink: 0;
}

/* CONTADOR TOTAL - COMPACTO */
.notes-filters-buttons__total-counter {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    flex-shrink: 0;
    height: 36px;
}

.notes-filters-buttons__total-counter i {
    font-size: 0.9375rem;
    color: #94a3b8;
}

.notes-filters-buttons__counter-number {
    font-weight: 700;
    font-size: 1rem;
    color: #64748b;
    line-height: 1;
}

/* RESPONSIVE */
@media (max-width: 768px) {
    .notes-filters-buttons__content-row {
        gap: 0.5rem;
        height: 44px;
    }

    .notes-filters-buttons__scroll-nav-btn {
        width: 32px;
        height: 32px;
    }

    .notes-filters-buttons__note-item {
        padding: 5px 10px;
        height: 32px;
    }

    .notes-filters-buttons__note-number {
        width: 22px;
        height: 22px;
        font-size: 0.6875rem;
    }

    .notes-filters-buttons__note-date {
        font-size: 0.75rem;
    }

    .notes-filters-buttons__total-counter {
        padding: 5px 10px;
        height: 32px;
    }
}

@media (max-width: 640px) {
    .notes-filters-buttons__content-row {
        gap: 0.375rem;
    }

    .notes-filters-buttons__scroll-nav-btn {
        width: 30px;
        height: 30px;
    }
}
</style>
